import * as _ from 'underscore';
import moment from 'moment';
import { getStore, getState } from '../../store';

import ACTIONS from './actions';
import { TYPES as PROGRAM_TYPES } from '../../../state/reducer/calendar/programTypes';

import * as CalendarState from '../calendar/calendar';
import * as Util from '../../../lib/designerUtils';
import * as ProgramService from '../../../api/service/programService';
import * as ToastState from './toast';
import ActionBarOptions from './actionBarOptions';

/**
 * Adds a 'exerciseKeys' property to each set in a session
 * @param {Object} block - The block to change
 * @return {Object} Updated block
 */
function addExerciseKeys(block) {
    const exerciseKeys = [];
    block.key = block.id;
    _.map(block.exercises, ex => {
        ex.key = ex.id;
        exerciseKeys.push(ex.id);
    });
    block.exerciseKeys = exerciseKeys;
    return block;
}
/**
 * Adds a 'setKeys' property to the session
 * @param {Object} session - The session to change
 * @return {Object} Updated session
 */
function addSetKeys(session) {
    const setKeys = [];
    _.map(session.sets, set => {
        setKeys.push(set.id);
    });
    session.setKeys = setKeys;
    return session;
}

export const clearSelected = () => {
    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_CLEAR_SELECTED_SESSIONS
    });
};

export const prepareSessionsForClipboard = sessions => {
    const items = [];
    let offset = 0;
    const dayList = getState().calendar.dayList;

    sessions = _.sortBy(sessions, s => s.data.timeline_day || s.data.date);

    for (let i = 0; i < sessions.length; i++) {
        const obj = sessions[i].data;
        const dayKey = Util.getDayKey(obj);
        const keyIndex = dayList.indexOf(dayKey);

        if (i === 0) {
            offset = keyIndex;
        }

        const item = {
            index: keyIndex - offset,
            dayKey,
            pwId: obj.id,
            obj
        };

        items.push(item);
    }
    return items;
};

export const copy = () => {
    const sessions = _.values(selectedSessions());
    const items = prepareSessionsForClipboard(sessions);

    if (!sessions.length) return;

    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_COPY_SELECTED_SESSIONS,
        copiedSessions: items
    });
    const message = `Copied ${_.keys(items).length} session(s).`;
    ToastState.showInfoMessage(message);
};

/**
 * Copy single workout
 * @param {Number} programWorkoutId
 * @param {Number} toWeekIndex
 * @param {Number} toDayIndex
 * @param {Boolean} hasMultiCopy
 */
export const copySingleProgramWorkout = (
    programWorkoutId,
    toWeekIndex,
    toDayIndex,
    hasMultiCopy,
    newDate = false
) => {
    const original = {
        fake: true
    };

    const programType = getState().calendar.infoTree.programType;
    let toDayData;
    const programId = getState().calendar.infoTree.team.id;

    switch (programType) {
        case PROGRAM_TYPES.TEAM:
        case PROGRAM_TYPES.CALENDAR:
        case PROGRAM_TYPES.COACH_ATHLETE_CALENDAR:
        case PROGRAM_TYPES.INVITED_ATHLETE_CALENDAR:
            toDayData = original;
            toDayData.date = newDate.format('YYYY-MM-DD');
            toDayData.day = parseInt(newDate.format('D'), 10);
            toDayData.dayIndex = toDayIndex;
            toDayData.month = parseInt(newDate.format('M'), 10);
            toDayData.weekIndex = toWeekIndex;
            toDayData.year = newDate.year();
            toDayData.published = 0;

            const needToAddToCurrentCalendar = CalendarState.isDateOnCalendar(
                toDayData.date
            );
            if (needToAddToCurrentCalendar)
                CalendarState.addFakeSessionToDay(toDayData);
            return ProgramService.copyProgramWorkoutToNewDay(
                programId,
                programWorkoutId,
                toDayData,
                null
            ).then(newSession => {
                if (!needToAddToCurrentCalendar) return;
                const key = Util.getDayKey(newSession);
                const day = getState().calendar.dayTree[key];
                newSession.key = newSession.id;
                // Update state
                return updateTeamProgramSessionState(day, newSession);
            });
        case PROGRAM_TYPES.FIXED:
            toDayData = original;
            toDayData.index = toDayIndex;
            toDayData.weekIndex = toWeekIndex;
            toDayData.timelineDay = toWeekIndex * 7 + toDayIndex + 1;
            toDayData.published = 1;

            if (isNaN(toWeekIndex)) {
                return ToastState.showWarningMessage(
                    'Cannot paste sessions beyond last week of program.'
                );
            }

            CalendarState.addFakeSessionToDay(toDayData);

            return ProgramService.copyProgramWorkoutToNewDay(
                programId,
                programWorkoutId,
                null,
                toDayData
            ).then(newSession => {
                const key = Util.getDayKeyFromTimelineDay(
                    newSession.timeline_day
                );
                const day = getState().calendar.dayTree[key];
                newSession.key = newSession.id;
                // Update state
                return updateTeamProgramSessionState(day, newSession);
            });
        default:
        //do nothing
    }
};

export const getClipboardContents = () => getState().clipboard.copiedSessions;

export const getSelectedSessions = () => {
    const sessions = _.values(selectedSessions());
    const sessionList = [];

    for (let i = 0; i < sessions.length; i++) {
        sessionList.push(sessions[i].data);
    }

    return sessionList;
};

export const getSelectedSessionIds = () => {
    let selectedSessions = getSelectedSessions();
    return _.map(selectedSessions, session => session.id);
};

const getStartDayIndex = (dayList, startDayKey) => {
    const startDayIndex = dayList.indexOf(startDayKey);
    if (startDayIndex >= 0) return startDayIndex;

    const startDate = moment(startDayKey, 'YYYY_M_D');
    const firstDateInList = moment(dayList[0], 'YYYY_M_D');
    return startDate.diff(firstDateInList, 'days');
};

const getPasteDay = (dayTree, pasteDayKey) => {
    const pasteDay = dayTree[pasteDayKey];
    if (!pasteDay) {
        const date = moment(pasteDayKey, 'YYYY_M_D');
        return {
            year: date.format('YYYY'),
            month: date.format('M'),
            day: date.format('D')
        };
    }
    return pasteDay;
};

const getPasteDayKey = (dayList, pasteDayIndex) => {
    const pasteDayKey = dayList[pasteDayIndex];
    if (!pasteDayKey) {
        const lastIndex = dayList[dayList.length - 1];
        const diff = pasteDayIndex - (dayList.length - 1);
        const date = moment(lastIndex, 'YYYY_M_D');
        date.add(diff, 'days');
        return date.format('YYYY_M_D');
    }
    return pasteDayKey;
};

export const getHoveredSessionIds = () => {
    let calendarState = getState().calendar;
    let dayKey = calendarState.uiTree.calendarHover;
    let day = dayKey && calendarState.dayTree[dayKey];

    if (!day) return [];

    let sessionKeys = day.items || [];
    return getSessionIdsFromKeys(sessionKeys);
};

export const getSessionIdsFromKeys = sessionKeys => {
    let allSessions = getState().calendar.sessionTree;
    let sessionIds = [];
    _.each(allSessions, (session, key) => {
        key = parseInt(key);
        if (sessionKeys.includes(key)) sessionIds.push(session.data.id);
    });
    return sessionIds;
};

export const select = selectedSessionIds => {
    const sessions = getSessions(selectedSessionIds);
    const calendarType = getCalendarType();
    const actionBarOptions = new ActionBarOptions(sessions, calendarType);
    const programExportEnabled = actionBarOptions.isProgramExportEnabled();
    const publishEnabled = actionBarOptions.isPublishEnabled();
    const unpublishEnabled = actionBarOptions.isUnpublishEnabled();

    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_SELECT_SESSIONS,
        selectedSessions: sessions,
        publishEnabled,
        unpublishEnabled,
        programExportEnabled
    });
};

const getCalendarType = () => getState().calendar.infoTree.programType;

export const selectFromRightClick = () => {
    let hoveredSessionIds = getHoveredSessionIds();
    let selectedSessionIds = getSelectedSessionIds();

    let intersection = _.intersection(hoveredSessionIds, selectedSessionIds);
    let sessionIds = intersection.length
        ? hoveredSessionIds.concat(selectedSessionIds)
        : hoveredSessionIds;

    return select(sessionIds);
};

export const updatePublishedStatus = async published => {
    const selectedSessions = getState().clipboard.selectedSessions;
    const sessionIds = _.map(selectedSessions, (session, key) => ({
        id: key,
        pwId: parseInt(session.data.id, 10)
    }));
    await CalendarState.bulkPublishSessions(sessionIds, published);
    clearSelected();
};

/**
 * Update state
 * @param {Object} day
 * @param {Object} session
 */
function updateTeamProgramSessionState(day, session, fromDay) {
    // Add setKeys property to new saved session
    addSetKeys(session);
    // Add exerciseKeys property to set's session
    _.map(session.sets, addExerciseKeys);
    CalendarState.addSessionFromCalendar(day, session, fromDay);
}

export const showRepeatDialog = () => {
    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_ENABLE_REPEAT_MODAL
    });
};

export const showExportAsProgram = open => {
    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_ENABLE_EXPORT_AS_PROGRAM_MODAL,
        isOpen: open
    });
};

export const cancelRepeatModal = () => {
    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_CANCEL_REPEAT_MODAL
    });
};

export const pasteSessions = (sessions, startDayKey) => {
    const dayList = getState().calendar.dayList;
    const dayTree = getState().calendar.dayTree;
    const startDayIndex = getStartDayIndex(dayList, startDayKey);

    // Copy workouts individually
    _.each(sessions, session => {
        const pasteDayIndex = startDayIndex + session.index;
        const pasteDayKey = getPasteDayKey(dayList, pasteDayIndex);
        const pasteDay = getPasteDay(dayTree, pasteDayKey);
        let pasteDate = false;
        let dayIndex, weekIndex;

        if (pasteDay.year && pasteDay.month && pasteDay.day) {
            // we are pasting on a calendar
            let month = pasteDay.month;
            let day = pasteDay.day;
            if (month < 10) {
                month = `0${month}`;
            }
            if (day < 10) {
                day = `0${day}`;
            }
            pasteDate = moment(
                `${pasteDay.year}-${month}-${day}`,
                'YYYY-MM-DD'
            );
            dayIndex = pasteDay.day;
            weekIndex = pasteDay.weekIndex;
        } else {
            // we are pasting on a program
            dayIndex = pasteDay.index;
            weekIndex = pasteDay.weekIndex;
        }

        const pwId = session.pwId;
        copySingleProgramWorkout(pwId, weekIndex, dayIndex, true, pasteDate);
    });
};

export const paste = () => {
    const contents = getClipboardContents();
    if (!contents || contents.length === 0) {
        return; // nothing to paste
    }
    if (contents.length === 1) {
        ToastState.showSuccessMessage('Pasting Session');
    } else {
        ToastState.showSuccessMessage(`Pasting ${contents.length} Sessions`);
    }

    const hoverKey = getState().calendar.uiTree.calendarHover;
    return pasteSessions(contents, hoverKey);
};

export const deleteSelected = () => {
    const sessions = selectedSessions();
    let delCount = 0;
    const promises = [];
    _.each(sessions, (session, key) => {
        const dayKey = Util.getDayKey(session.data);
        const q = deleteWorkout(key, dayKey);
        delCount++;
        promises.push(q);
    });
    clearSelected();
    Promise.all(promises).then(() => {
        const message = `Successfully deleted ${delCount} session(s).`;
        ToastState.showSuccessMessage(message);
    });
};

const deleteWorkout = (sessionId, dayKey) => {
    const session = getState().calendar.sessionTree[sessionId];
    const day = getState().calendar.dayTree[dayKey];
    const dispatch = getStore().dispatch;
    CalendarState.removeSession(dispatch, day, session.data);
    const programId = getState().calendar.infoTree.programId;
    ProgramService.removeProgramWorkout(programId, session.data.id);
};

export const cancelDeleteDialog = () => {
    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_CANCEL_DELETE_DIALOG
    });
};

export const showDeleteDialog = () => {
    let sessions = _.values(selectedSessions());
    if (sessions.length) {
        getStore().dispatch({
            type: ACTIONS.CLIPBOARD_ENABLE_DELETE_DIALOG
        });
    }
};

export const disableCalendarHotkeys = () => {
    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_DISABLE_CALENDAR_HOTKEYS
    });
};

export const enableCalendarHotkeys = () => {
    getStore().dispatch({
        type: ACTIONS.CLIPBOARD_ENABLE_CALENDAR_HOTKEYS
    });
};

export const toggleCalendarHotkeys = enabled => {
    if (enabled) return enableCalendarHotkeys();
    return disableCalendarHotkeys();
};

const getSessions = sessionIds => {
    const allSessions = getState().calendar.sessionTree;
    const sessions = {};
    _.each(allSessions, (session, key) => {
        if (sessionIds.includes(session.data.id)) sessions[key] = session;
    });
    return sessions;
};

const selectedSessions = () => getState().clipboard.selectedSessions;
